import Commons from '../../../../Services/Common/Common';
import dayjs from 'dayjs';
import Store from '../../../../Store/Store';

/**
 * HEA_BASIC_02 스키마
 * - fields: 폼/그리드에서 공통으로 쓰는 필드 정의
 *   - initial: 초기값
 *   - label: 필드명
 *   - required: 필수 입력 여부
 *   - requiredWhen: 언제 필수인지 조건
 *
 * - mapper: 저장/삭제 API payload 생성 규칙
 *   - mapRowsBySchema(rows, mapper, context)로 payload를 만듬
 *   - context에는 userInfo 등이 들어오며(mapRowsBySchema에 기본족으로 정의되어 있음),
 *     화면에서 추가로 필요한 값(custCd 등)을 context로 넘겨줄 수 있다.
 */
export const HEA_BASIC_02_Schema = {
  fields: {
    no: { initial: '', label: 'NO' },
    hscCd: { initial: '', label: '특수건강진단코드' },
    hscId: { initial: '', label: '사용자ID' },
    custCd: { initial: '', label: '사업장코드' },
    depNm1: { initial: '', label: '본부' },
    depNm2: { initial: '', label: '부서' },
    depNm3: { initial: '', label: '팀' },
    depNm4: { initial: '', label: '파트' },
    position: { initial: '', label: '직책' },
    name: { initial: '', label: '성명', required: 'Y' },
    checkType: { initial: '0', label: '배치전/특수', required: 'Y' },
    hscGubun: { initial: 'A', label: '건강관리 구분', required: 'Y' },
    workEnvironmentType: { initial: {}, label: '소음/분진/야간작업/화학물질 취급 작업 체크', required: 'Y' },
    hcSubstance: { initial: '', label: '취급물질' },
    hcPost: { initial: '', label: '배치 후 첫번째 특수건강진단 주기' },
    hcCycle: { initial: '', label: '검진주기', required: 'Y' },
    hcDate: { initial: '', label: '검진일', required: 'Y' },
    hcEffeDate: { initial: '', label: '유효일자', required: 'Y' },
    hcAlarmYn: { initial: '', label: '알람여부(Y/N)', required: 'Y' },
    hcCheckupCenter: { initial: '', label: '검진기관', required: 'Y' },
    hcCheckupAttachmentFileName: { initial: '', label: '검진표 첨부', required: 'Y' },
    hcPostcareAttachmentFileName: { initial: '', label: '사후관리조치 첨부', required: 'Y' },
    hcRemark: { initial: '', label: '비고' },
    alarmId: { initial: '', label: '알람ID' },
  },

  mapper: {
    search1: {
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
      SearchText: (_, context) =>
        Commons.EncryptAES(context.search?.searchText || '') || '',
      HcYear: (_, context) =>
        Commons.EncryptAES(context.search?.year || dayjs().format('YYYY')) ||
        '',
    },
    search2: {
      UserId: (_, context) => context.userInfo?.userid || '',
    },
    delete1: {
      HscCd: (row) => Commons.EncryptAES(row.hscCd || '') || '',
      AlarmId: (row) =>
        Commons.EncryptAES(row.alarmId ? row.alarmId.toString() : '0') || '',
    },
    save1: {
      HscCd: (row) => {
        const hscCd = row.hscCd;
        const hscCdValue =
          hscCd && hscCd.toString().trim() !== '' ? hscCd.toString() : '0';
        return Commons.EncryptAES(hscCdValue) || '';
      },
      HscId: (row) => Commons.EncryptAES(row.hscId || '') || '',
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
      CheckType: (row) => {
        // CHECK_TYPE (0/1 또는 '배치전'/'특수') → 0: 배치전, 1: 특수
        let checkType = '0'; // 기본값 '0' (배치전)
        const beforePlacement = row.checkType;
        if (
          beforePlacement === '0' ||
          beforePlacement === 0 ||
          beforePlacement === '배치전'
        ) {
          checkType = '0';
        } else if (
          beforePlacement === '1' ||
          beforePlacement === 1 ||
          beforePlacement === '특수'
        ) {
          checkType = '1';
        }
        return Commons.EncryptAES(checkType) || '';
      },
      HscGubun: (row) => {
        // HSC_GUBUN에서 코드값만 추출 (예: "A(이상없음)" -> "A", "D1 (직업병 유소견자)" -> "D1")
        const mgmtGubun = row.hscGubun || '';
        if (!mgmtGubun) return '';
        // 'A(이상없음)' 형식인 경우 명시적으로 'A' 반환
        if (
          mgmtGubun === 'A(이상없음)' ||
          mgmtGubun.toString().includes('A(이상없음)')
        ) {
          return Commons.EncryptAES('A') || '';
        }
        // 이미 코드값인지 확인 (A, D1, D2, C1, C2, R 중 하나)
        const codeList = ['A', 'D1', 'D2', 'C1', 'C2', 'R'];
        if (codeList.includes(mgmtGubun.toString().trim())) {
          return Commons.EncryptAES(mgmtGubun.toString().trim()) || '';
        }
        // 코드만 추출 (A, D1, D2, C1, C2, R)
        const codeMatch = mgmtGubun.toString().match(/^([ADC12R]+)/);
        const codeValue = codeMatch
          ? codeMatch[1]
          : mgmtGubun.toString().trim();
        return Commons.EncryptAES(codeValue) || '';
      },
      WorkEnvironmentType: (row) => {
        // DB 저장: '0,1,2,3' 형식 문자열 (0:소음, 1:분진, 2:야간작업, 3:화학물질)
        // 그리드/엑셀에서 소음 Y→0, 분진 Y→1, 야간작업 Y→2, 화학물질 Y→3 로 매핑
        let value = '';
        if (
          row.noiseYn !== undefined ||
          row.dustYn !== undefined ||
          row.nightYn !== undefined ||
          row.chemicalYn !== undefined
        ) {
          const parts = [];
          if (row.noiseYn === 'Y') parts.push('0');
          if (row.dustYn === 'Y') parts.push('1');
          if (row.nightYn === 'Y') parts.push('2');
          if (row.chemicalYn === 'Y') parts.push('3');
          value = parts.join(',');
        } else {
          const raw = (row.workEnvironmentType || '').toString().trim();
          const codes = raw
            ? raw.split(',').map((s) => s.trim()).filter((s) => ['0', '1', '2', '3'].includes(s))
            : [];
          value = [...new Set(codes)].sort().join(',');
        }
        return Commons.EncryptAES(value) || '';
      },
      HcSubstance: (row) => Commons.EncryptAES(row.hcSubstance || '') || '',
      HcPost: (row) => Commons.EncryptAES(row.hcPost || '') || '',
      HcCycle: (row) => Commons.EncryptAES(row.hcCycle || '') || '',
      HcDate: (row) => {
        if (!row.hcDate || !dayjs(row.hcDate).isValid()) return '';
        return Commons.EncryptAES(dayjs(row.hcDate).format('YYYY-MM-DD')) || '';
      },
      HcYear: (row, context) => {
        const year =
          row.hcDate && dayjs(row.hcDate).isValid()
            ? dayjs(row.hcDate).format('YYYY')
            : context.search?.year || '';
        return Commons.EncryptAES(year) || '';
      },
      HcEffeDate: (row) => {
        if (!row.hcEffeDate || !dayjs(row.hcEffeDate).isValid()) return '';
        return Commons.EncryptAES(dayjs(row.hcEffeDate).format('YYYY-MM-DD')) || '';
      },
      HcAlarmYn: (row) => Commons.EncryptAES(row.hcAlarmYn || '') || '',
      HcCheckupCenter: (row) =>
        Commons.EncryptAES(row.hcCheckupCenter || '') || '',
      HcCheckupAttachmentFileName: (row) => {
        const reportFile = row.hcCheckupAttachmentFileName || {};
        if (typeof reportFile === 'string') return Commons.EncryptAES(reportFile) || '';
        return Commons.EncryptAES(reportFile.FILE_NAME || '') || '';
      },
      HcCheckupAttachmentSaveName: (row) => {
        const reportFile = row.hcCheckupAttachmentSaveName || row.hcCheckupAttachmentFileName || {};
        if (typeof reportFile === 'string') return Commons.EncryptAES(reportFile) || '';
        return Commons.EncryptAES(reportFile.SAVE_NAME || reportFile.FILE_NAME || '') || '';
      },
      HcCheckupAttachmentFilePath: (row, context) => {
        // 새 파일 업로드 시: previewFilePath에서 saveName 추출하여 최종 경로 생성
        const previewPath = row.hcCheckupPreviewFilePath || '';
        if (previewPath) {
          const saveName = previewPath.split('/').pop() || '';
          const finalPath = `HealthCheckup/${context.search?.custCd}/HEA_BASIC_02/Checkup/${saveName}`;
          return Commons.EncryptAES(finalPath) || '';
        }

        // 기존 파일 유지 시: hcCheckupAttachmentFilePath 직접 사용
        const reportFile = row.hcCheckupAttachmentFilePath || row.hcCheckupAttachmentFileName || {};
        const reportFilePath = typeof reportFile === 'string' ? reportFile : reportFile.FILE_PATH || '';
        if (!reportFilePath) {
          const reportSaveName = typeof reportFile === 'string' ? reportFile : reportFile.SAVE_NAME || reportFile.FILE_NAME || '';
          if (reportSaveName) {
            return Commons.EncryptAES(`HealthCheckup/${context.search?.custCd}/HEA_BASIC_02/Checkup/${reportSaveName}`) || '';
          }
          return '';
        }
        return Commons.EncryptAES(reportFilePath) || '';
      },
      HcPostcareAttachmentFileName: (row) => {
        const postcareFile = row.hcPostcareAttachmentFileName || {};
        if (typeof postcareFile === 'string') return Commons.EncryptAES(postcareFile) || '';
        return Commons.EncryptAES(postcareFile.FILE_NAME || '') || '';
      },
      HcPostcareAttachmentSaveName: (row) => {
        const postcareFile = row.hcPostcareAttachmentSaveName || row.hcPostcareAttachmentFileName || {};
        if (typeof postcareFile === 'string') return Commons.EncryptAES(postcareFile) || '';
        return Commons.EncryptAES(postcareFile.SAVE_NAME || postcareFile.FILE_NAME || '') || '';
      },
      HcPostcareAttachmentFilePath: (row, context) => {
        // 새 파일 업로드 시: previewFilePath에서 saveName 추출하여 최종 경로 생성
        const previewPath = row.hcPostcarePreviewFilePath || '';
        if (previewPath) {
          const saveName = previewPath.split('/').pop() || '';
          const finalPath = `HealthCheckup/${context.search?.custCd}/HEA_BASIC_02/PostCare/${saveName}`;
          return Commons.EncryptAES(finalPath) || '';
        }

        // 기존 파일 유지 시: hcPostcareAttachmentFilePath 직접 사용
        const postcareFile = row.hcPostcareAttachmentFilePath || row.hcPostcareAttachmentFileName || {};
        const postcareFilePath = typeof postcareFile === 'string' ? postcareFile : postcareFile.FILE_PATH || '';
        if (!postcareFilePath) {
          const postcareSaveName = typeof postcareFile === 'string' ? postcareFile : postcareFile.SAVE_NAME || postcareFile.FILE_NAME || '';
          if (postcareSaveName) {
            return Commons.EncryptAES(`HealthCheckup/${context.search?.custCd}/HEA_BASIC_02/PostCare/${postcareSaveName}`) || '';
          }
          return '';
        }
        return Commons.EncryptAES(postcareFilePath) || '';
      },
      HcRemark: (row) => Commons.EncryptAES(row.hcRemark || '') || '',
      AlarmId: (row) =>
        Commons.EncryptAES(row.alarmId ? row.alarmId.toString() : '0') || '',
      UserId: (_, context) => context.userInfo?.userid || '',
      CoCd: (_, context) =>
        Commons.EncryptAES(context.search?.coCd || '') || '',
      EmployeeNo: (row) => Commons.EncryptAES(row.employeeNo || '') || '',
      ProperName: () => {
        const companyInfo = Store.getState()?.BaseInfo?.companyInfo[0];
        return Commons.EncryptAES(companyInfo?.properName || '') || '';
      },
      DepNm1: (row) => Commons.EncryptAES(row.depNm1 || '') || '',
      DepNm2: (row) => Commons.EncryptAES(row.depNm2 || '') || '',
      DepNm3: (row) => Commons.EncryptAES(row.depNm3 || '') || '',
      DepNm4: (row) => Commons.EncryptAES(row.depNm4 || '') || '',
      Position: (row) => Commons.EncryptAES(row.position || '') || '',
      Name: (row) => Commons.EncryptAES(row.name || '') || '',
    },
    save2: {
      HscCd: (row) => Commons.EncryptAES(row.hscCd || '') || '',
      HcEffeDate: (row) => {
        if (!row.hcEffeDate || !dayjs(row.hcEffeDate).isValid()) return '';
        return Commons.EncryptAES(dayjs(row.hcEffeDate).format('YYYY-MM-DD')) || '';
      },
      HcAlarmYn: (row) => Commons.EncryptAES(row.hcAlarmYn || '') || '',
      CalId: (row) => Commons.EncryptAES(row.calId ? row.calId.toString() : '') || '',
      AlarmId: (row) =>
        Commons.EncryptAES(row.alarmId ? row.alarmId.toString() : '0') || '',
      HealthName: (row) => Commons.EncryptAES(row.name || '') || '',
      UserId: (_, context) => context.userInfo?.userid || '',
    },
  },
};
